import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  FileText,
  Calendar,
  Award,
  Users,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  X,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  Download,
} from "lucide-react";
import ProcessNumber from "../components/common/ProcessNumber";
import CNJSearchInput from "../components/common/CNJSearchInput";
import DateInputBR from "../components/common/DateInputBR";
import apiService from "../services/api";
import processesService from "../services/processesService";
import PartnerLawyerBadge from "../components/common/PartnerLawyerBadge";
import ProcessLeadBadge from "../components/common/ProcessLeadBadge";
import { lawyerPartnersService } from "../services/lawyerPartnersService";
import { findMatchingLawyerPartners } from "../utils/lawyerPartners";
import { cleanCNJForSearch } from "../hooks/useSearchCNJ";
import { decodeHtmlEntities } from "../utils/htmlEntities";
import { safeParseAnalysis } from "../utils/processAnalysis";
import {
  buildProcessosListQuery,
  buildProcessosReturnTo,
  readProcessosListQuery,
  rememberProcessosReturnTo,
} from "../utils/processosListNavigation";

const CANONICAL_ESFERAS = ["Estadual", "Federal", "Trabalhista"];

const VIEW_TABS = [
  { id: "ativos", label: "Ativos" },
  { id: "rejeitados", label: "Rejeitados" },
  { id: "todos", label: "Todos" },
];

function formatPartyName(value) {
  const decoded = decodeHtmlEntities(value);
  if (typeof decoded !== "string") return "";
  return decoded.replace(/\s+/g, " ").trim();
}

function ProcessPartiesCell({ partes }) {
  const ativo = formatPartyName(partes?.ativo);
  const passivo = formatPartyName(partes?.passivo);

  return (
    <div className="max-w-[16rem] space-y-1">
      <p
        className="text-xs leading-snug text-gray-900 break-words"
        title={ativo || undefined}
      >
        <span className="font-semibold text-gray-500">Ativo:</span>{" "}
        {ativo || <span className="text-gray-400">—</span>}
      </p>
      <p
        className="text-xs leading-snug text-gray-900 break-words"
        title={passivo || undefined}
      >
        <span className="font-semibold text-gray-500">Passivo:</span>{" "}
        {passivo || <span className="text-gray-400">—</span>}
      </p>
    </div>
  );
}

const emptyProcessFilters = {
  searchTerm: "",
  statusFilter: "",
  viabilityFilter: "",
  tribunalFilter: "",
  esferaFilter: "",
  scoreRange: { min: "", max: "" },
  dateRange: { start: "", end: "" },
};

const Processos = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialListQuery] = useState(() => readProcessosListQuery(searchParams));
  const syncedListQueryRef = useRef(searchParams.toString());
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros e busca
  const [searchTerm, setSearchTerm] = useState(initialListQuery.filters.searchTerm);
  const [statusFilter, setStatusFilter] = useState(initialListQuery.filters.statusFilter);
  const [viabilityFilter, setViabilityFilter] = useState(
    initialListQuery.filters.viabilityFilter,
  );
  const [tribunalFilter, setTribunalFilter] = useState(
    initialListQuery.filters.tribunalFilter,
  );
  const [esferaFilter, setEsferaFilter] = useState(initialListQuery.filters.esferaFilter);
  const [scoreRange, setScoreRange] = useState(initialListQuery.filters.scoreRange);
  const [dateRange, setDateRange] = useState(initialListQuery.filters.dateRange);
  const [appliedFilters, setAppliedFilters] = useState(initialListQuery.filters);

  // Paginação
  const [pagination, setPagination] = useState({
    page: initialListQuery.page,
    limit: 25,
    total: 0,
    totalPages: 0,
    hasMore: false,
  });

  // Ordenação
  const [sortBy, setSortBy] = useState(initialListQuery.sortBy);
  const [sortOrder, setSortOrder] = useState(initialListQuery.sortOrder);

  // Estados para UI
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [stats, setStats] = useState(null);
  const [lawyerPartners, setLawyerPartners] = useState([]);
  const [selectedView, setSelectedView] = useState(initialListQuery.view);
  const [statusActionProcessId, setStatusActionProcessId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const loadLawyerPartners = async () => {
    try {
      const data = await lawyerPartnersService.getPartners();
      setLawyerPartners(data || []);
    } catch (error) {
      setLawyerPartners([]);
    }
  };

  const buildProcessQueryParams = ({ includePagination = false, pageOverride } = {}) => {
    const params = new URLSearchParams();

    if (includePagination) {
      const currentPage = pageOverride ?? pagination.page;
      params.append("page", currentPage.toString());
      params.append("limit", pagination.limit.toString());
    }

    params.append("sortBy", sortBy);
    params.append("sortOrder", sortOrder);

    if (appliedFilters.searchTerm) {
      params.append("numeroProcesso", appliedFilters.searchTerm);
    }
    if (selectedView === "rejeitados") {
      params.append("status", "REJEITADO");
    } else {
      if (appliedFilters.statusFilter) {
        params.append("status", appliedFilters.statusFilter);
      }
      if (selectedView === "ativos") {
        params.append("excludeStatus", "REJEITADO");
      }
    }
    if (appliedFilters.viabilityFilter) {
      params.append("viabilidade", appliedFilters.viabilityFilter);
    }
    if (appliedFilters.tribunalFilter) {
      params.append("tribunal", appliedFilters.tribunalFilter);
    }
    if (appliedFilters.esferaFilter) {
      params.append("esfera", appliedFilters.esferaFilter);
    }
    if (appliedFilters.scoreRange?.min) {
      params.append("scoreMin", appliedFilters.scoreRange.min);
    }
    if (appliedFilters.scoreRange?.max) {
      params.append("scoreMax", appliedFilters.scoreRange.max);
    }
    if (appliedFilters.dateRange?.start) {
      params.append("dataInicio", appliedFilters.dateRange.start);
    }
    if (appliedFilters.dateRange?.end) {
      params.append("dataFim", appliedFilters.dateRange.end);
    }

    return params;
  };

  const buildExportFilters = () => {
    const params = buildProcessQueryParams();
    const filters = {};
    for (const [key, value] of params.entries()) {
      filters[key] = value;
    }
    return filters;
  };

  // Carregar processos
  const loadProcesses = async (pageOverride) => {
    try {
      setLoading(true);
      setError(null);

      const currentPage = pageOverride ?? pagination.page;
      const params = buildProcessQueryParams({
        includePagination: true,
        pageOverride: currentPage,
      });

      const data = await apiService.get(`/processes?${params.toString()}`);

      setProcesses(data.data || []);
      setPagination((prev) => ({
        ...prev,
        ...(data.pagination || {}),
        page: data.pagination?.page || currentPage,
      }));
    } catch (error) {
      console.error("Erro ao carregar processos:", error);
      setError("Erro ao carregar processos. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // Carregar estatísticas
  const loadStats = async () => {
    try {
      const data = await apiService.get("/processes/stats");
      setStats(data);
    } catch (error) {
      console.error("Erro ao carregar estatísticas:", error);
    }
  };

  const openProcess = (processId) => {
    const returnTo = buildProcessosReturnTo({
      page: pagination.page,
      view: selectedView,
      sortBy,
      sortOrder,
      filters: appliedFilters,
    });
    rememberProcessosReturnTo(processId, returnTo);
    navigate(`/processo/${processId}`, { state: { returnTo } });
  };

  // Efeitos
  useEffect(() => {
    loadProcesses();
  }, [pagination.page, sortBy, sortOrder, appliedFilters, selectedView]);

  useEffect(() => {
    const next = buildProcessosListQuery({
      page: pagination.page,
      view: selectedView,
      sortBy,
      sortOrder,
      filters: appliedFilters,
    });

    if (syncedListQueryRef.current === next) return;

    syncedListQueryRef.current = next;
    setSearchParams(next, { replace: true });
  }, [pagination.page, selectedView, sortBy, sortOrder, appliedFilters, setSearchParams]);

  useEffect(() => {
    loadStats();
    loadLawyerPartners();
  }, []);

  // Handlers
  const handleSearch = (event) => {
    event?.preventDefault?.();

    const normalizedSearchTerm = cleanCNJForSearch(searchTerm?.trim?.() || "");

    setAppliedFilters({
      searchTerm: normalizedSearchTerm,
      statusFilter,
      viabilityFilter,
      tribunalFilter,
      esferaFilter,
      scoreRange: { ...scoreRange },
      dateRange: { ...dateRange },
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setSearchTerm(emptyProcessFilters.searchTerm);
    setStatusFilter(emptyProcessFilters.statusFilter);
    setViabilityFilter(emptyProcessFilters.viabilityFilter);
    setTribunalFilter(emptyProcessFilters.tribunalFilter);
    setEsferaFilter(emptyProcessFilters.esferaFilter);
    setScoreRange({ ...emptyProcessFilters.scoreRange });
    setDateRange({ ...emptyProcessFilters.dateRange });
    setAppliedFilters({
      searchTerm: emptyProcessFilters.searchTerm,
      statusFilter: emptyProcessFilters.statusFilter,
      viabilityFilter: emptyProcessFilters.viabilityFilter,
      tribunalFilter: emptyProcessFilters.tribunalFilter,
      esferaFilter: emptyProcessFilters.esferaFilter,
      scoreRange: { ...emptyProcessFilters.scoreRange },
      dateRange: { ...emptyProcessFilters.dateRange },
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleViewChange = (viewId) => {
    setSelectedView(viewId);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleExportExcel = async () => {
    if (exporting || pagination.total === 0) return;

    setExporting(true);
    setExportError("");
    try {
      await processesService.exportExcel(buildExportFilters());
    } catch (error) {
      console.error("Erro ao exportar processos em Excel:", error);
      setExportError(
        error?.response?.data?.message ||
          "Não foi possível exportar a planilha Excel.",
      );
    } finally {
      setExporting(false);
    }
  };

  const handleProcessStatusAction = async (process, action) => {
    if (!process?.id || statusActionProcessId) return;

    const isRejectAction = action === "reject";
    const confirmationMessage = isRejectAction
      ? "Descartar este caso? Ele será movido para a aba Rejeitados e os leads gerados automaticamente a partir dele serão ocultados da página de leads."
      : "Restaurar este caso para análise concluída? Os leads ocultados pelo descarte voltarão a aparecer.";

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    try {
      setStatusActionProcessId(process.id);
      setError(null);

      if (isRejectAction) {
        await processesService.rejectProcess(process.id);
      } else {
        await processesService.restoreProcess(process.id);
      }

      await Promise.all([loadProcesses(), loadStats()]);
    } catch (error) {
      console.error("Erro ao atualizar status do processo:", error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Não foi possível atualizar o status do processo.";
      alert(message);
    } finally {
      setStatusActionProcessId(null);
    }
  };

  const canRejectProcess = (process) =>
    selectedView !== "rejeitados" &&
    process?.status === "ANALISE_CONCLUIDA" &&
    (!process?.leadSummary?.hasLeads ||
      process?.leadSummary?.conversionMode === "AUTO");

  const canRestoreProcess = (process) => process?.status === "REJEITADO";

  // Funções de formatação
  const getStatusColor = (status) => {
    switch (status) {
      case "AGUARDANDO_ANALISE":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "AGUARDANDO_VALOR_OFICIO":
        return "bg-amber-100 text-amber-800 border-amber-200";
      case "EM_ANALISE":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "APROVADO":
        return "bg-green-100 text-green-800 border-green-200";
      case "REJEITADO":
        return "bg-red-100 text-red-800 border-red-200";
      case "ANALISE_FALHOU":
        return "bg-red-100 text-red-800 border-red-200";
      case "CONVERTIDO_LEAD":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "ANALISE_CONCLUIDA":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "AGUARDANDO_ANALISE":
      case "AGUARDANDO_VALOR_OFICIO":
        return <Clock className="w-4 h-4" />;
      case "EM_ANALISE":
        return <Eye className="w-4 h-4" />;
      case "APROVADO":
        return <CheckCircle className="w-4 h-4" />;
      case "REJEITADO":
      case "ANALISE_FALHOU":
        return <XCircle className="w-4 h-4" />;
      case "CONVERTIDO_LEAD":
        return <Users className="w-4 h-4" />;
      case "ANALISE_CONCLUIDA":
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "AGUARDANDO_ANALISE":
        return "Aguardando Análise";
      case "AGUARDANDO_VALOR_OFICIO":
        return "Aguardando valor do ofício";
      case "EM_ANALISE":
        return "Em Análise";
      case "APROVADO":
        return "Aprovado";
      case "REJEITADO":
        return "Rejeitado";
      case "ANALISE_FALHOU":
        return "Falha na análise";
      case "CONVERTIDO_LEAD":
        return "Convertido em Lead";
      case "ANALISE_CONCLUIDA":
        return "Análise Concluída";
      default:
        return status;
    }
  };

  const getScoreBadge = (score, classification) => {
    const getScoreInfo = (score) => {
      if (score >= 76)
        return {
          label: "EXCEPCIONAL",
          color: "bg-purple-100 text-purple-800",
          bgColor: "bg-purple-600",
        };
      if (score >= 66)
        return {
          label: "PROVÁVEL",
          color: "bg-green-100 text-green-800",
          bgColor: "bg-green-600",
        };
      if (score >= 41)
        return {
          label: "POSSÍVEL",
          color: "bg-blue-100 text-blue-800",
          bgColor: "bg-blue-600",
        };
      if (score >= 11)
        return {
          label: "REMOTO",
          color: "bg-orange-100 text-orange-800",
          bgColor: "bg-orange-600",
        };
      return {
        label: "INAPTO",
        color: "bg-red-100 text-red-800",
        bgColor: "bg-red-600",
      };
    };

    const scoreInfo = getScoreInfo(score);

    return (
      <div className="flex flex-col items-center gap-1">
        <div
          className={`px-2 py-1 rounded-full text-xs font-medium ${scoreInfo.color}`}
        >
          {score}/100
        </div>
        <div
          className={`px-2 py-0.5 rounded text-xs font-medium text-white ${scoreInfo.bgColor}`}
        >
          {scoreInfo.label}
        </div>
      </div>
    );
  };

  const enrichedProcesses = useMemo(() =>
    processes.map((process) => ({
      ...process,
      matchedPartners: findMatchingLawyerPartners({
        partners: lawyerPartners,
        content: [process.numeroProcesso, process.resumo, process.conteudo].filter(Boolean).join(" "),
      }),
    })),
  [processes, lawyerPartners]);

  const selectedProcessAnalysis = useMemo(
    () => safeParseAnalysis(selectedProcess?.analise),
    [selectedProcess],
  );

  const selectedProcessMemorando = useMemo(() => {
    const raw =
      selectedProcessAnalysis?.memorandoPadrao?.text ??
      selectedProcessAnalysis?.memorandoTrabalhista?.text ??
      null;
    // NÃO decodificar aqui para manter consistência com `ProcessoDetalhes.jsx`
    // e evitar divergência de exibição entre telas. A decodificação deve ocorrer
    // uma única vez no momento da renderização.
    return raw == null ? null : String(raw);
  }, [selectedProcessAnalysis]);

  const formatValue = (value) => {
    if (!value) return "N/A";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const getProcessActivityDate = (process) => {
    if (process.analisadoEm) {
      return {
        date: process.analisadoEm,
        label: "Análise",
      };
    }
    if (process.updatedAt) {
      return {
        date: process.updatedAt,
        label: "Atualizado",
      };
    }
    return {
      date: process.createdAt,
      label: "Criado",
    };
  };

  const formatProcessDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const activeFilterCount =
    (statusFilter ? 1 : 0) +
    (viabilityFilter ? 1 : 0) +
    (esferaFilter ? 1 : 0) +
    (scoreRange.min || scoreRange.max ? 1 : 0) +
    (dateRange.start || dateRange.end ? 1 : 0);

  // Ícone de ordenação
  const getSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === "asc" ? (
      <ChevronUp className="w-3 h-3 ml-1 inline" />
    ) : (
      <ChevronDown className="w-3 h-3 ml-1 inline" />
    );
  };

  if (loading && processes.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="text-gray-600">Carregando processos...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Processos {pagination.total > 0 && `(${pagination.total})`}
            </h1>
            <p className="text-gray-600">
              Gerencie processos convertidos das publicações e realize análises
              técnicas
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Estatísticas rápidas */}
            {stats && (
              <div className="flex gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {stats.total || 0}
                  </div>
                  <div className="text-sm text-gray-600">Total</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">
                    {stats.byStatus?.AGUARDANDO_ANALISE || 0}
                  </div>
                  <div className="text-sm text-gray-600">Pendentes</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {stats.byStatus?.ANALISE_CONCLUIDA || 0}
                  </div>
                  <div className="text-sm text-gray-600">Análises concluídas</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {stats.byStatus?.REJEITADO || 0}
                  </div>
                  <div className="text-sm text-gray-600">Rejeitados</div>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleExportExcel}
                disabled={loading || exporting || pagination.total === 0}
                className="inline-flex items-center whitespace-nowrap px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                <Download
                  className={`w-4 h-4 mr-2 shrink-0 ${exporting ? "animate-pulse" : ""}`}
                />
                {exporting ? "Exportando..." : "Exportar Excel"}
              </button>
              <button
                type="button"
                onClick={() => loadProcesses()}
                disabled={loading}
                className="inline-flex items-center whitespace-nowrap px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 shrink-0 ${loading ? "animate-spin" : ""}`}
                />
                Atualizar
              </button>
            </div>
          </div>
        </div>
        {exportError && (
          <p className="mt-2 text-sm text-red-600">{exportError}</p>
        )}
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="flex-1">
                <CNJSearchInput
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onSubmit={handleSearch}
                  placeholder="Buscar por número do processo..."
                  showCNJIndicator={true}
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Buscar
              </button>
            </form>
          </div>

          {/* Abas de visualização */}
          <div className="flex flex-wrap items-center gap-2">
            {VIEW_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleViewChange(tab.id)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedView === tab.id
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Filtros rápidos */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                showFilters ||
                statusFilter ||
                viabilityFilter ||
                esferaFilter ||
                scoreRange.min ||
                scoreRange.max ||
                dateRange.start ||
                dateRange.end
                  ? "bg-blue-600 text-white hover:bg-blue-700"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtros
              {activeFilterCount > 0 && (
                <span className="bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[1.25rem] text-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Painel de Filtros */}
        {showFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  disabled={selectedView === "rejeitados"}
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                >
                  <option value="">
                    {selectedView === "rejeitados"
                      ? "Apenas rejeitados"
                      : "Todos os Status"}
                  </option>
                  {selectedView !== "rejeitados" && (
                    <>
                      <option value="AGUARDANDO_ANALISE">Aguardando Análise</option>
                      <option value="AGUARDANDO_VALOR_OFICIO">Aguardando valor do ofício</option>
                      <option value="EM_ANALISE">Em Análise</option>
                      <option value="ANALISE_FALHOU">Falha na análise</option>
                      <option value="APROVADO">Aprovado</option>
                      <option value="REJEITADO">Rejeitado</option>
                      <option value="ANALISE_CONCLUIDA">Análise Concluída</option>
                      <option value="CONVERTIDO_LEAD">Convertido em Lead</option>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Esfera
                </label>
                <select
                  value={esferaFilter}
                  onChange={(e) => setEsferaFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todas as Esferas</option>
                  {CANONICAL_ESFERAS.map((esfera) => (
                    <option key={esfera} value={esfera}>
                      {esfera}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Viabilidade
                </label>
                <select
                  value={viabilityFilter}
                  onChange={(e) => setViabilityFilter(e.target.value)}
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Todas as Viabilidades</option>
                  <option value="ALTA">Alta</option>
                  <option value="MEDIA">Média</option>
                  <option value="BAIXA">Baixa</option>
                  <option value="INCONCLUSIVO">Inconclusivo</option>
                  <option value="INVIAVEL">Inviável</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Score Mínimo
                </label>
                <input
                  type="number"
                  value={scoreRange.min}
                  onChange={(e) =>
                    setScoreRange((prev) => ({ ...prev, min: e.target.value }))
                  }
                  placeholder="0"
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 bg-white text-gray-900 placeholder:text-gray-500 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Score Máximo
                </label>
                <input
                  type="number"
                  value={scoreRange.max}
                  onChange={(e) =>
                    setScoreRange((prev) => ({ ...prev, max: e.target.value }))
                  }
                  placeholder="100"
                  min="0"
                  max="100"
                  className="w-full px-3 py-2 bg-white text-gray-900 placeholder:text-gray-500 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data inicial
                </label>
                <DateInputBR
                  value={dateRange.start}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, start: e.target.value }))
                  }
                  max={dateRange.end || undefined}
                  className="w-full px-3 py-2 bg-white text-gray-900 placeholder:text-gray-500 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data final
                </label>
                <DateInputBR
                  value={dateRange.end}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, end: e.target.value }))
                  }
                  min={dateRange.start || undefined}
                  className="w-full px-3 py-2 bg-white text-gray-900 placeholder:text-gray-500 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={handleSearch}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Aplicar Filtros
              </button>
              <button
                type="button"
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tabela de Processos */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {error && (
          <div className="p-4 bg-red-50 border-b border-red-200">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Carregando processos...</span>
          </div>
        ) : enrichedProcesses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider w-1/5">
                    <button
                      onClick={() => handleSort("numeroProcesso")}
                      className="flex items-center hover:text-blue-600 transition-colors"
                    >
                      Processo / Data
                      {getSortIcon("numeroProcesso")}
                    </button>
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider w-1/5">
                    <button
                      onClick={() => handleSort("status")}
                      className="flex items-center hover:text-blue-600 transition-colors"
                    >
                      Status / Tribunal
                      {getSortIcon("status")}
                    </button>
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Partes
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-1/6">
                    <button
                      onClick={() => handleSort("score")}
                      className="flex items-center justify-center hover:text-blue-600 transition-colors w-full"
                    >
                      Score
                      {getSortIcon("score")}
                    </button>
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-1/12">
                    Valor / Viabilidade
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-bold text-gray-700 uppercase tracking-wider w-1/12">
                    Atualização
                  </th>
                  <th className="px-3 py-2 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-1/12">
                    Ação
                  </th>
                </tr>
              </thead>
              <tbody>
                {enrichedProcesses.map((process, index) => {
                  const activity = getProcessActivityDate(process);

                  return (
                  <React.Fragment key={process.id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2">
                        <div>
                          <ProcessNumber
                            processNumber={process.numeroProcesso}
                            className="text-xs text-gray-600 leading-tight"
                            copyable={true}
                          />
                          <div className="flex items-center gap-1 mt-0.5">
                            <p className="text-xs font-medium text-gray-900 leading-tight">
                              {new Date(process.createdAt).toLocaleDateString(
                                "pt-BR",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                }
                              )}
                            </p>
                          </div>
                          {process.matchedPartners?.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {process.matchedPartners.slice(0, 1).map((partner) => (
                                <PartnerLawyerBadge
                                  key={partner.id}
                                  partner={partner}
                                  compact={true}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div>
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                              process.status
                            )}`}
                          >
                            {getStatusIcon(process.status)}
                            {getStatusLabel(process.status)}
                          </span>
                          {process.leadSummary?.hasLeads && (
                            <div className="mt-1">
                              <ProcessLeadBadge
                                leadSummary={process.leadSummary}
                                processId={process.id}
                              />
                            </div>
                          )}
                          <p className="text-xs text-gray-600 mt-0.5">
                            {process.tribunal || "N/A"}
                          </p>
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <ProcessPartiesCell partes={process.partes} />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div>
                          {getScoreBadge(
                            process.score,
                            process.scoreClassification
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="text-xs leading-tight">
                          <div className="text-xs font-medium text-gray-900">
                            {formatValue(process.valorEstimado)}
                          </div>
                          <div className="mt-0.5">
                            {process.viabilidade ? (
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  process.viabilidade === "ALTA"
                                    ? "bg-green-100 text-green-800"
                                    : process.viabilidade === "MEDIA"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : process.viabilidade === "BAIXA"
                                    ? "bg-orange-100 text-orange-800"
                                    : process.viabilidade === "INCONCLUSIVO"
                                    ? "bg-slate-100 text-slate-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {process.viabilidade === "MEDIA"
                                  ? "Média"
                                  : process.viabilidade.charAt(0) +
                                    process.viabilidade.slice(1).toLowerCase()}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">
                                Não analisado
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-xs leading-tight">
                          <p className="text-xs font-medium text-gray-900">
                            {formatProcessDate(activity.date)}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {activity.label}
                          </p>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          <button
                            onClick={() => openProcess(process.id)}
                            className="inline-flex items-center px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
                          >
                            Ver
                          </button>

                          {canRejectProcess(process) && (
                            <button
                              type="button"
                              onClick={() =>
                                handleProcessStatusAction(process, "reject")
                              }
                              disabled={statusActionProcessId === process.id}
                              className="inline-flex items-center px-2 py-1 bg-red-50 text-red-700 border border-red-200 rounded text-xs hover:bg-red-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                              title="Rejeitar caso sem enviar para leads"
                            >
                              {statusActionProcessId === process.id
                                ? "Salvando..."
                                : "Rejeitar"}
                            </button>
                          )}

                          {canRestoreProcess(process) && (
                            <button
                              type="button"
                              onClick={() =>
                                handleProcessStatusAction(process, "restore")
                              }
                              disabled={statusActionProcessId === process.id}
                              className="inline-flex items-center px-2 py-1 bg-white text-slate-700 border border-slate-300 rounded text-xs hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                              title="Restaurar caso rejeitado"
                            >
                              {statusActionProcessId === process.id
                                ? "Salvando..."
                                : "Restaurar"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Classifications row - similar to publications */}
                    {process.classifications &&
                      process.classifications.length > 0 && (
                        <tr className="hover:bg-gray-50 transition-colors">
                          <td
                            colSpan="7"
                            className="px-3 py-2 pt-0 bg-white border-t-0"
                          >
                            <div className="flex flex-wrap gap-1 -mt-1">
                              {process.classifications
                                .sort((a, b) => {
                                  const priorityOrder = {
                                    urgente: 1,
                                    alta: 2,
                                    média: 3,
                                    media: 3,
                                    normal: 4,
                                    baixa: 5,
                                    ia: 6,
                                  };
                                  const aPriority =
                                    priorityOrder[a.priority?.toLowerCase()] ||
                                    7;
                                  const bPriority =
                                    priorityOrder[b.priority?.toLowerCase()] ||
                                    7;
                                  if (aPriority !== bPriority) {
                                    return aPriority - bPriority;
                                  }
                                  return a.name.localeCompare(b.name);
                                })
                                .map((classification, index) => {
                                  const getPriorityColor = (priority) => {
                                    switch (priority?.toLowerCase()) {
                                      case "alta":
                                        return "bg-red-100 text-red-700";
                                      case "urgente":
                                        return "bg-gray-100 text-gray-700";
                                      case "média":
                                      case "media":
                                        return "bg-yellow-100 text-yellow-700";
                                      case "ia":
                                        return "bg-black text-white";
                                      case "normal":
                                      case "baixa":
                                        return "bg-blue-100 text-blue-700";
                                      default:
                                        return "bg-gray-100 text-gray-700";
                                    }
                                  };

                                  return (
                                    <span
                                      key={
                                        classification._id ||
                                        classification.id ||
                                        index
                                      }
                                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getPriorityColor(
                                        classification.priority
                                      )}`}
                                      title={`Prioridade: ${classification.priority}`}
                                    >
                                      {classification.name}
                                    </span>
                                  );
                                })}
                            </div>
                          </td>
                        </tr>
                      )}

                    {/* Divider after each record group */}
                    {index < processes.length - 1 && (
                      <tr>
                        <td
                          colSpan="7"
                          className="border-b border-gray-200"
                        ></td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nenhum processo encontrado
            </h3>
            <p className="text-gray-500">
              {searchTerm || statusFilter || viabilityFilter || esferaFilter || dateRange.start || dateRange.end
                ? "Tente ajustar os filtros de busca"
                : selectedView === "rejeitados"
                  ? "Nenhum processo rejeitado no momento"
                  : "Converta publicações em processos para começar"}
            </p>
          </div>
        )}

        {/* Paginação */}
        {processes.length > 0 && pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Mostrando {(pagination.page - 1) * pagination.limit + 1} a{" "}
              {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
              de {pagination.total} resultados
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <span className="px-3 py-1 text-sm text-gray-700 bg-white border rounded">
                {pagination.page} de {pagination.totalPages}
              </span>

              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Detalhes do Processo */}
      {selectedProcess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Detalhes do Processo
                </h3>
                <button
                  onClick={() => setSelectedProcess(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">
                    Informações Básicas
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-600">
                        Número:
                      </span>
                      <ProcessNumber
                        processNumber={selectedProcess.numeroProcesso}
                        className="ml-2 text-blue-600 font-medium"
                        copyable={true}
                      />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">
                        Status:
                      </span>
                      <span
                        className={`ml-2 inline-flex items-center gap-2 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                          selectedProcess.status
                        )}`}
                      >
                        {getStatusIcon(selectedProcess.status)}
                        {getStatusLabel(selectedProcess.status)}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">
                        Score:
                      </span>
                      <span
                        className={`ml-2 font-semibold ${getScoreColor(
                          selectedProcess.score
                        )}`}
                      >
                        {selectedProcess.score}/100
                      </span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">
                        Tribunal:
                      </span>
                      <span className="ml-2 text-gray-900">
                        {selectedProcess.tribunal || "N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">
                        Vara:
                      </span>
                      <span className="ml-2 text-gray-900">
                        {selectedProcess.vara || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-3">
                    Análise Técnica
                  </h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm font-medium text-gray-600">
                        Viabilidade:
                      </span>
                      {selectedProcess.viabilidade ? (
                        <span
                          className={`ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            selectedProcess.viabilidade === "ALTA"
                              ? "bg-green-100 text-green-800"
                              : selectedProcess.viabilidade === "MEDIA"
                              ? "bg-yellow-100 text-yellow-800"
                              : selectedProcess.viabilidade === "BAIXA"
                              ? "bg-orange-100 text-orange-800"
                              : selectedProcess.viabilidade === "INCONCLUSIVO"
                              ? "bg-slate-100 text-slate-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {selectedProcess.viabilidade === "MEDIA"
                            ? "Média"
                            : selectedProcess.viabilidade.charAt(0) +
                              selectedProcess.viabilidade
                                .slice(1)
                                .toLowerCase()}
                        </span>
                      ) : (
                        <span className="ml-2 text-gray-400">
                          Não analisado
                        </span>
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">
                        Valor Estimado:
                      </span>
                      <span className="ml-2 text-gray-900 font-medium">
                        {formatValue(selectedProcess.valorEstimado)}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-gray-600">
                        Analisado por:
                      </span>
                      <span className="ml-2 text-gray-900">
                        {selectedProcess.analisadoPor || "Não analisado"}
                      </span>
                    </div>
                    {selectedProcess.analisadoEm && (
                      <div>
                        <span className="text-sm font-medium text-gray-600">
                          Data da Análise:
                        </span>
                        <span className="ml-2 text-gray-900">
                          {new Date(
                            selectedProcess.analisadoEm
                          ).toLocaleDateString("pt-BR")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {(selectedProcessMemorando || selectedProcess.resumo) && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">
                    {selectedProcessMemorando ? "Parecer" : "Resumo"}
                  </h4>
                  {selectedProcessMemorando ? (
                    <pre className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                      {decodeHtmlEntities(String(selectedProcessMemorando))}
                    </pre>
                  ) : (
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {decodeHtmlEntities(String(selectedProcess.resumo))}
                    </p>
                  )}
                </div>
              )}

              {selectedProcess.recomendacao && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">
                    Recomendação
                  </h4>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {decodeHtmlEntities(String(selectedProcess.recomendacao))}
                  </p>
                </div>
              )}

              {selectedProcess.observacoes && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">
                    Observações
                  </h4>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {decodeHtmlEntities(String(selectedProcess.observacoes))}
                  </p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
              <button
                onClick={() => setSelectedProcess(null)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
              >
                Fechar
              </button>
              {selectedProcess.status === "AGUARDANDO_ANALISE" && (
                <button
                  onClick={() => {
                    // TODO: Implementar análise técnica
                    setSelectedProcess(null);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Iniciar Análise Técnica
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Processos;
